// Month/year picker for the Token Usage page. Allows selecting any of the
// last N calendar months (not strictly limited to `getAvailablePeriods()`)
// so a period with no archive can still be chosen — this is what exercises
// the historical-not-found (404) empty state, matching how a real archive
// list could be incomplete or briefly stale relative to what's queryable.
import { useMemo } from 'react';
import { Dropdown, Option, Field, Badge, makeStyles, tokens } from '@fluentui/react-components';
import type { AvailablePeriod } from '../../types/tokenusage';
import { formatPeriodLabel, monthName, periodKey } from './formatUsage';

const LOOKBACK_MONTHS = 12;

const useStyles = makeStyles({
  container: { display: 'flex', alignItems: 'flex-end', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  optionRow: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
});

export interface PeriodOption {
  year: number;
  month: number;
  period: string;
  isCurrent: boolean;
  isFinal: boolean | null; // null = unknown until queried (not in availablePeriods)
}

function buildOptions(
  availablePeriods: AvailablePeriod[] | undefined,
  nowYear: number,
  nowMonth: number,
  selectedYear: number,
  selectedMonth: number,
): PeriodOption[] {
  const availableByKey = new Map((availablePeriods ?? []).map((p) => [p.period, p]));
  const options: PeriodOption[] = [];
  const seenKeys = new Set<string>();

  for (let back = 0; back < LOOKBACK_MONTHS; back++) {
    // Anchor the lookback window on the page's authoritative current period
    // (nowYear/nowMonth — derived by TokenUsage.tsx from the polled
    // current-month document's own `period` field), NOT a fresh `new
    // Date()` read here. Two independent clock reads can disagree across a
    // UTC month boundary or during server regeneration lag, which would
    // make this picker's "current" badge/anchor drift from what the page
    // actually treats as the live period (see PR #96968 thread 958235).
    const d = new Date(Date.UTC(nowYear, nowMonth - 1 - back, 1));
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const key = periodKey(year, month);
    const known = availableByKey.get(key);
    options.push({
      year,
      month,
      period: key,
      isCurrent: year === nowYear && month === nowMonth,
      isFinal: known ? known.isFinal : year === nowYear && month === nowMonth ? false : null,
    });
    seenKeys.add(key);
  }

  // Guarantee the currently-selected period always has a matching Option,
  // even if it falls outside the lookback window above (e.g. a stale
  // `availablePeriods`/anchor combination during a month-boundary
  // rollover) — otherwise Dropdown's `selectedOptions` would point at a
  // period with no corresponding <Option>, leaving the control showing no
  // highlighted selection.
  const selectedKeyForOptions = periodKey(selectedYear, selectedMonth);
  if (!seenKeys.has(selectedKeyForOptions)) {
    const known = availableByKey.get(selectedKeyForOptions);
    const isSelectedCurrent = selectedYear === nowYear && selectedMonth === nowMonth;
    options.unshift({
      year: selectedYear,
      month: selectedMonth,
      period: selectedKeyForOptions,
      isCurrent: isSelectedCurrent,
      isFinal: known ? known.isFinal : isSelectedCurrent ? false : null,
    });
  }

  return options;
}

export interface PeriodPickerProps {
  year: number;
  month: number;
  /**
   * The page's authoritative "current period" (derived from the polled
   * current-month document's own `period` field, not a local clock read).
   * Drives the lookback anchor and the "current" badge so this component
   * never disagrees with TokenUsage.tsx about which period is live.
   */
  nowYear: number;
  nowMonth: number;
  availablePeriods: AvailablePeriod[] | undefined;
  onChange: (year: number, month: number) => void;
  label?: string;
  disabled?: boolean;
}

export function PeriodPicker(props: PeriodPickerProps) {
  const { year, month, nowYear, nowMonth, availablePeriods, onChange, label = 'Period', disabled } = props;
  const styles = useStyles();
  const options = useMemo(
    () => buildOptions(availablePeriods, nowYear, nowMonth, year, month),
    [availablePeriods, nowYear, nowMonth, year, month],
  );
  const selectedKey = periodKey(year, month);
  const selectedLabel = formatPeriodLabel(selectedKey);

  return (
    <div className={styles.container}>
      <Field label={label}>
        <Dropdown
          value={selectedLabel}
          selectedOptions={[selectedKey]}
          disabled={disabled}
          onOptionSelect={(_, data) => {
            const opt = options.find((o) => o.period === data.optionValue);
            if (opt) onChange(opt.year, opt.month);
          }}
        >
          {options.map((opt) => (
            <Option key={opt.period} value={opt.period} text={`${monthName(opt.month)} ${opt.year}`}>
              <span className={styles.optionRow}>
                {monthName(opt.month)} {opt.year}
                {opt.isCurrent && (
                  <Badge appearance="tint" color="brand" size="small">current</Badge>
                )}
              </span>
            </Option>
          ))}
        </Dropdown>
      </Field>
    </div>
  );
}

export default PeriodPicker;
